import React, { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router";
import { DotLegalFullScreenSpinner } from "@dotlegal/dotlegal-ui-components";
import ProcessingActivityOverview from "./processingActivity/processingActivityOverview/ProcessingActivityOverview";
import ProcessingActivity from "./processingActivity/ProcessingActivity";
import ErrorPage from "./errorPage/ErrorPage";
import { useTranslation } from "./localization/useTranslation";
import LegalEntity from "./legalEntity/LegalEntity";
import SettingsRedirect from "./privacySettings/settings/SettingsRedirect";
import RedirectLegacyLegalEntityUrls from "./RedirectLegacyLegalEntityUrls";
import Users from "./user/users/Users";
import User from "./user/User";
import Customers from "./customer/customers/Customers";
import Record from "./record/Record";
import ChangeOrganization from "./auth/ChangeOrganization";
import { MasterData } from "./masterData/MasterData";
import Purposes from "./masterData/purpose/purposes/Purposes";
import SecurityMeasures from "./masterData/securityMeasure/securityMeasures/SecurityMeasures";
import BusinessAreas from "./masterData/businessArea/businessAreas/BusinessAreas";
import { DeletionPeriods } from "./masterData/deletionPeriod/deletionPeriods/DeletionPeriods";
import { DataCategories } from "./masterData/dataCategory/dataCategories/DataCategories";
import DataSubjects from "./masterData/dataSubject/dataSubjects/DataSubjects";
import ProcessingActivityRiskScenariosDialog from "./riskAssessment/processingActivityRiskScenariosDialog/ProcessingActivityRiskScenariosDialog";
import ProcessingActivityRiskQuestionDialog from "./riskAssessment/riskAssessmentQuestionDialog/RiskAssessmentQuestionDialog";
import RiskAssessmentTemplate from "./riskAssessment/riskAssessmentTemplate/RiskAssessmentTemplate";
import SystemOverview from "./system/systemOverview/SystemOverview";
import System from "./system/systemEdit/SystemEdit";
import NationalLaws from "./masterData/nationalLaw/nationalLaws/NationalLaws";
import Legislations from "./masterData/legislation/legislations/Legislations";
import Activities from "./annualWheel/activities/Activities";
import AnnualWheelPDF from "./annualWheel/AnnualWheelPDF";
import AddEditCustomerDialog from "./customer/addEditCustomerDialog/AddEditCustomerDialog";
import Notification from "./notification/Notification";
import CustomLists from "./customLists/CustomLists";
import { CallPage, CustomListType } from "./customLists/CustomLists.types";
import CustomListView from "./customLists/customListView/CustomListView";
import Roles from "./roles/Roles";
import HelpTexts from "./masterData/helpText/helpTexts/HelpTexts";
import ProcessingActivityStatistics from "./processingActivity/processingActivityStatistics/ProcessingActivityStatistics";
import RegistrationOfResearchProjectReport from "./processingActivityReport/registrationOfResearchProjectReport/RegistrationOfResearchProjectReport";
import InternalAcquisitionOfSignatureReport from "./processingActivityReport/internalAcquisitionOfSignatureReport/InternalAcquisitionOfSignatureReport";
import ProcessingActivityRiskPDF from "./riskAssessment/riskAssessmentDialog/PDF/RiskAssessmentPDF";
import ProcessingActivityStatisticsPdf from "./processingActivity/processingActivityStatistics/pdf/ProcessingActivityStatisticsPdf";
import Certifications from "./masterData/certification/certifications/Certifications";
import Areas from "./masterData/area/areas/Areas";
import RiskArea from "./risk/riskArea/RiskArea";
import RiskAssessmentsOverview from "./risk/riskAssessmentsOverview/RiskAssessmentsOverview";
import RiskManagementReport from "./risk/riskManagementReport/RiskManagementReport";
import RiskObservation from "./risk/riskObservation/RiskObservations";
import RiskAreas from "./riskSettings/riskArea/riskAreas/RiskAreas";
import RiskScenarios from "./riskSettings/riskScenarios/RiskScenarios";
import RiskConsequences from "./riskSettings/riskConsequence/RiskConsequences";
import RiskAssessmentVersions from "./masterData/riskAssessmentVersion/riskAssessmentVersions/RiskAssessmentVersions";
import ResearchEthicsApprovalReport from "./processingActivityReport/researchEthicsApprovalReport/ResearchEthicsApprovalReport";
import { ProcessingActivityStatus } from "./processingActivity/processingActivities/ProcessingActivities.types";
import EditLegalEntity from "./legalEntity/editLegalEntity/EditLegalEntity";
import LegalEntityClassification from "./legalEntity/LegalEntityClassification.tsx";
import LegalEntityAuditGroups from "./legalEntityAuditGroup/legalEntityAuditGroups/LegalEntityAuditGroups.tsx";
import LegalEntityAuditGroupOverview from "./legalEntityAuditGroup/legalEntityAuditGroupOverview/LegalEntityAuditGroupOverview.tsx";
import LegalEntityAuditGroupDraftOverview from "./legalEntityAuditGroup/legalEntityAuditGroupDraftOverview/LegalEntityAuditGroupDraftOverview.tsx";
import InternalAssessmentGroupOverview from "./internalAssessment/internalAssessmentGroupOverview/InternalAssessmentGroupOverview";
import InternalAssessmentGroupDraftOverview from "./internalAssessment/internalAssessmentGroupDraftOverview/InternalAssessmentGroupDraftOverview";
import QuestionnairesListOverview from "./questionnaireAdministration/QuestionnairesListOverview.tsx";
import { useFeatureFlags } from "./auth/planProvider/PlanProvider.tsx";
import { FeatureFlag } from "./auth/planProvider/PlanProvider.types.ts";
import LegalEntities from "./legalEntity/legalEntities/LegalEntities";
import { LegalEntityScope } from "./legalEntity/legalEntities/LegalEntities.types";
import Tags from "./masterData/tag/Tags.tsx";
import DataTypesList from "./masterData/dataCategory/dataTypes/DataTypesList.tsx";
import DocumentTypes from "./masterData/documentType/documentTypes/DocumentTypes.tsx";
import IncidentTypes from "./masterData/incidentType/incidentTypes/IncidentTypes.tsx";
import IncidentAffectedServices from "./masterData/incidentAffectedService/incidentAffectedServices/IncidentAffectedServices.tsx";
import AddTaskMenuItemProvider from "./common/components/addTaskMenuItem/AddTaskMenuItemContext.tsx";
import ContractTypes from "./masterData/contractType/contractTypes/ContractTypes.tsx";
import LegalEntityTypes from "./masterData/legalEntityType/legalEntityTypes/LegalEntityTypes.tsx";
import PaymentTerms from "./masterData/paymentTerm/paymentTerms/PaymentTerms.tsx";
import FrameworkTemplateOverview from "./frameworkTemplate/frameworkTemplateOverview/FrameworkTemplateOverview.tsx";
import Templates from "./frameworkTemplate/templates/Templates.tsx";
import FrameworkTemplateCategories from "./frameworkTemplate/frameworkTemplateOverview/frameworkTemplateCategory/FrameworkTemplateCategories.tsx";
import AnnualWheelActivityTemplatesList from "./masterData/annualWheelActivityTemplate/AnnualWheelActivityTemplatesList.tsx";
import AnnualWheelActivityStandardTemplateList from "./masterData/annualWheelActivityStandardTemplate/AnnualWheelActivityStandardTemplateList.tsx";
import FrameworkCategories from "./framework/frameworkCategory/FrameworkCategories.tsx";
import FrameworkControlOverview from "./framework/frameworkControl/frameworkControlOverview/FrameworkControlOverview.tsx";
import EditEntityTemplate from "./entityTemplates/EditEntityTemplate.tsx";
import InputFields from "./entityTemplates/inputFields/InputFields.tsx";
import EntityTemplateSteps from "./entityTemplates/entityTemplateSteps/EntityTemplateSteps.tsx";
import EntityTemplates from "./entityTemplates/EntityTemplates.tsx";
import ApprovalOfQualityProjectReport from "./processingActivityReport/approvalOfQualityProjectReport/ApprovalOfQualityProjectReport.tsx";
import PenneoCallback from "./contract/penneoCallback/PenneoCallback.tsx";
import EmailCustomization from "./emailCustomization/EmailCustomization.tsx";
import AiAdministration from "./aiAdministration/AiAdministration.tsx";
import ApiKeys from "./apiKeys/ApiKeys.tsx";
import AdvancedDocumentSearch from "./common/components/documentPreview/advancedDocumentSearch/AdvancedDocumentSearch.tsx";
import Export from "./export/Export.tsx";
import SystemNotifications from "./notification/systemNotifications/SystemNotifications.tsx";
import BestPracticeDocumentTemplates from "./bestPracticeDocumentTemplate/BestPracticeDocumentTemplates.tsx";
import SettingsLayout from "./privacySettings/settings/settingsLayout/SettingsLayout.tsx";
import ExternalUsers from "./externalUser/externalUsers/ExternalUsers.tsx";
import { TemplateEntityType } from "./entityTemplates/EntityTemplate.types.ts";
import { useStartPage } from "./user/hooks/useStartPage.ts";

const DataProcessing = lazy(() => import("./dataProcessing/DataProcessing"));
const LegacyProcessingActivities = lazy(() => import("./processingActivity/processingActivities/LegacyProcessingActivities"));
const Processes = lazy(() => import("./process/processes/Processes"));
const ProcessOverview = lazy(() => import("./process/processOverview/ProcessOverview"));
const EditProcess = lazy(() => import("./process/editProcess/EditProcess"));
const RiskAssessments = lazy(() => import("./riskAssessment/riskAssessments/RiskAssessments"));
const Systems = lazy(() => import("./system/systems/Systems"));
const AnnualWheel = lazy(() => import("./annualWheel/AnnualWheel"));
const TaskManagement = lazy(() => import("./taskManagement/TaskManagement"));
const Policies = lazy(() => import("./policy/policies/Policies"));
const PolicyOverview = lazy(() => import("./policy/policyOverview/PolicyOverview"));
const PolicyCompareVersions = lazy(() => import("./policy/policyCompareVersions/PolicyCompareVersions"));
const IncidentLog = lazy(() => import("./incidentLog/incidentLog/IncidentLog"));
const IncidentLogs = lazy(() => import("./incidentLog/incidentLogs/IncidentLogs"));
const IncidentLogOverview = lazy(() => import("./incidentLog/incidentLogOverview/IncidentLogOverview"));
const EditIncidentLog = lazy(() => import("./incidentLog/editIncidentLog/EditIncidentLog"));
const InternalAssessments = lazy(() => import("./internalAssessment/InternalAssessments"));
const Dashboard = lazy(() => import("./dashboard/Dashboard.tsx"));
const ContractDashboard = lazy(() => import("./dashboard/contractDashboard/ContractDashboard.tsx"));
const Contracts = lazy(() => import("./contract/Contracts.tsx"));
const ContractOverview = lazy(() => import("./contract/contractOverview/ContractOverview.tsx"));
const Frameworks = lazy(() => import("./framework/frameworks/Frameworks.tsx"));
const FrameworkOverview = lazy(() => import("./framework/frameworkOverview/FrameworkOverview.tsx"));
const Contract = lazy(() => import("./contract/contract/Contract.tsx"));

function RouterSwitch() {
    const { translateString } = useTranslation();
    const navigate = useNavigate();
    const { getStartPagePath } = useStartPage();
    const featureFlags = useFeatureFlags();
    const hasIncidentLogTemplates = featureFlags.isEnabled(FeatureFlag.IncidentLogTemplates);
    const hasNewProcessingActivity = featureFlags.isEnabled(FeatureFlag.NewProcessingActivity);
    const hasProcessTemplates = featureFlags.isEnabled(FeatureFlag.ProcessTemplates);
    const hasContractDashboard = featureFlags.isEnabled(FeatureFlag.ContractDashboard);

    const getPermissionDefaultPage = () => {
        const startPagePath = getStartPagePath();

        if (startPagePath) {
            return <Navigate to={startPagePath} />;
        }

        return <ErrorPage errorMessage={translateString("forbiddenAccess")} image="NoAccess" />;
    };

    // <AddTaskMenuItemProvider> is places in here so that app is not rerendered when <AddTaskMenuItemProvider> is
    return (
        <AddTaskMenuItemProvider>
            <Suspense fallback={<DotLegalFullScreenSpinner />}>
                <Routes>
                    <Route path="penneo/callback" element={<PenneoCallback />} />
                    <Route path="privacy">
                        <Route path="dashboard">
                            <Route path="" element={<Dashboard />} />
                            {hasContractDashboard && <Route path="contracts" element={<ContractDashboard />} />}
                        </Route>

                        {/* SIMPLIFY when FeatureFlag.NewProcessingActivity is removed - keep DataProcessing, delete the legacy page and the processingactivities-legacy route */}
                        <Route path="processingactivities" element={hasNewProcessingActivity ? <DataProcessing /> : <LegacyProcessingActivities />} />
                        {/* Not linked from anywhere: lets the new overview be compared with the legacy one while the flag is on */}
                        <Route path="processingactivities-legacy" element={<LegacyProcessingActivities />} />

                        {hasProcessTemplates && (
                            <Route path="processes">
                                <Route path="" element={<Processes />} />
                                <Route path=":id/overview" element={<ProcessOverview />} />
                                <Route path=":id/edit" element={<EditProcess />} />
                            </Route>
                        )}

                        <Route path="processingactivity">
                            <Route path=":id">
                                <Route path="overview" element={<ProcessingActivityOverview />} />
                                <Route path="edit" element={<ProcessingActivity processingActivityValidation={false} />} />
                                <Route path="validate" element={<ProcessingActivity processingActivityValidation />} />
                            </Route>
                        </Route>

                        <Route path="systems" element={<Systems />} />
                        <Route path="system">
                            <Route path=":id">
                                <Route path="overview" element={<SystemOverview />} />
                                <Route path="edit" element={<System />} />
                            </Route>
                        </Route>

                        {/* SIMPLIFY when FeatureFlag.NewProcessingActivity is removed - the record lives in DataProcessing, delete this route, the legacy record page (record/, DataProcessorRecord/, DataControllerRecord/DataControllerRecord.tsx) and point the record menu links at the DataProcessing record view */}
                        <Route path="record" element={<Record />} />

                        {/* The external detail routes have no list of their own: the External list links its rows
                            here by design. */}
                        <Route path="legal-entities">
                            <Route path=":id" element={<LegalEntity legalEntityScope={LegalEntityScope.External} />} />
                            <Route path=":id/edit" element={<EditLegalEntity legalEntityScope={LegalEntityScope.External} />} />
                            <Route path=":id/classification/*" element={<LegalEntityClassification legalEntityScope={LegalEntityScope.External} />}></Route>
                            <Route path="" element={<Navigate to="/privacy/external-legal-entities" replace />} />
                        </Route>

                        <Route path="external-legal-entities" element={<LegalEntities scope={LegalEntityScope.External} />} />
                        <Route path="customer-legal-entities/*" element={<RedirectLegacyLegalEntityUrls />} />
                        <Route path="other-legal-entities/*" element={<RedirectLegacyLegalEntityUrls />} />

                        <Route path="group-entities">
                            <Route path=":id" element={<LegalEntity legalEntityScope={LegalEntityScope.GroupEntity} />} />
                            <Route path=":id/edit" element={<EditLegalEntity legalEntityScope={LegalEntityScope.GroupEntity} />} />
                            <Route path=":id/classification/*" element={<LegalEntityClassification legalEntityScope={LegalEntityScope.GroupEntity} />}></Route>
                            <Route path="" element={<LegalEntities scope={LegalEntityScope.GroupEntity} />} />
                        </Route>

                        <Route path="risk-assessments" element={<RiskAssessments />} />

                        <Route path="risk-management-report" element={<RiskManagementReport />} />

                        <Route path="observations" element={<RiskObservation />} />
                        {/* Observations lived under the risk module before; keeps bookmarks to the old address working. */}
                        <Route path="risk-observations" element={<Navigate to="/privacy/observations" replace />} />

                        <Route path="risk/:riskAreaId" element={<RiskArea />} />

                        <Route path="risk/:riskAreaId/:entityType/:entityId" element={<RiskAssessmentsOverview />} />

                        <Route element={<SettingsLayout />}>
                            <Route path="settings">
                                <Route path="" element={<SettingsRedirect />} />
                                <Route path="contract-input-fields" element={<InputFields templateEntityType={TemplateEntityType.Contract} />} />
                                <Route path="asset-input-fields" element={<InputFields templateEntityType={TemplateEntityType.System} />} />
                                <Route path="legal-entity-input-fields" element={<InputFields templateEntityType={TemplateEntityType.LegalEntity} />} />
                                <Route path="incident-log-input-fields" element={<InputFields templateEntityType={TemplateEntityType.IncidentLog} />} />
                                <Route path="process-input-fields" element={<InputFields templateEntityType={TemplateEntityType.Process} />} />
                                <Route path="entity-templates">
                                    <Route path=":id/edit" element={<EditEntityTemplate />} />
                                    <Route path=":id/steps" element={<EntityTemplateSteps />} />
                                    <Route path=":type" element={<EntityTemplates />} />
                                </Route>
                                <Route path="system-notifications" element={<SystemNotifications />} />
                                <Route path="system-notifications" element={<SystemNotifications />} />
                            </Route>

                            <Route path="annual-wheel-activity-standard-templates" element={<AnnualWheelActivityStandardTemplateList />} />
                            <Route path="email-customization" element={<EmailCustomization />} />
                            <Route path="ai-administration" element={<AiAdministration />} />
                            <Route path="api-keys" element={<ApiKeys />} />
                            <Route path="risk-settings/risk-areas" element={<RiskAreas />} />
                            <Route path="risk-settings/risk-scenarios" element={<RiskScenarios />} />
                            <Route path="risk-settings/risk-consequences" element={<RiskConsequences />} />
                            <Route path="users" element={<Users />} />
                            <Route path="user">
                                <Route path=":id" element={<User />} />
                            </Route>

                            <Route path="external-users" element={<ExternalUsers />} />

                            <Route path="customers" element={<Customers />} />
                            <Route
                                path="customer/:id/edit"
                                element={
                                    <AddEditCustomerDialog
                                        selectedCustomer={undefined}
                                        onCloseDialog={() => {
                                            navigate(-1);
                                        }}
                                    />
                                }
                            />

                            <Route path="masterdata">
                                <Route path="" element={<MasterData />} />
                                <Route path="purposes" element={<Purposes />} />
                                <Route path="business-areas" element={<BusinessAreas />} />
                                <Route path="security-measures" element={<SecurityMeasures />} />
                                <Route path="deletion-periods" element={<DeletionPeriods />} />
                                <Route path="data-categories" element={<DataCategories />} />
                                <Route path="data-types" element={<DataTypesList />} />
                                <Route path="data-subjects" element={<DataSubjects />} />
                                <Route path="document-types" element={<DocumentTypes />} />
                                <Route path="incident-types" element={<IncidentTypes />} />
                                <Route path="incident-affected-services" element={<IncidentAffectedServices />} />
                                <Route path="national-laws" element={<NationalLaws />} />
                                <Route path="legislations" element={<Legislations />} />
                                <Route path="help-texts" element={<HelpTexts />} />
                                <Route path="certifications" element={<Certifications />} />
                                <Route path="areas" element={<Areas />} />
                                <Route path="risk-assessment-versions" element={<RiskAssessmentVersions />} />
                                <Route path="tags" element={<Tags />} />
                                <Route path="contract-types" element={<ContractTypes />} />
                                <Route path="legal-entity-types" element={<LegalEntityTypes />} />
                                <Route path="payment-terms" element={<PaymentTerms />} />
                                <Route path="annual-wheel-activity-templates" element={<AnnualWheelActivityTemplatesList />} />

                                <Route path="obligation-templates" element={<AnnualWheelActivityTemplatesList isObligation />} />
                                <Route path="best-practice-document-templates" element={<BestPracticeDocumentTemplates />} />
                            </Route>

                            <Route path="roles" element={<Roles />} />
                            <Route path="role">
                                <Route path=":id/edit" element={<Roles />} />
                                <Route path=":roleid/edit-permission" element={<Roles />} />
                            </Route>

                            <Route path="framework-templates">
                                <Route path="" element={<Templates />} />
                                <Route path=":id">
                                    <Route path="" element={<FrameworkTemplateOverview />} />
                                    <Route path="categories" element={<FrameworkTemplateCategories />} />
                                </Route>
                            </Route>
                        </Route>

                        <Route path="questionnaires/*" element={<QuestionnairesListOverview />} />
                        <Route path="change-organization" element={<ChangeOrganization />} />
                        <Route path="annual-wheel" element={<AnnualWheel />} />
                        <Route path="activities" element={<Activities />} />
                        <Route path="task-management" element={<TaskManagement />} />

                        <Route path="annual-wheel-PDF" element={<AnnualWheelPDF />} />
                        <Route path="notifications" element={<Notification />} />
                        <Route path="custom-processing-activity-lists" element={<CustomLists listType={CustomListType.ProcessingActivity} />} />
                        <Route path="custom-list-view/:customListId" element={<CustomListView />} />
                        <Route path="custom-company-lists" element={<CustomLists listType={CustomListType.Company} />} />
                        <Route
                            path="custom-group-company-lists"
                            element={<CustomLists listType={CustomListType.Company} callPage={CallPage.GroupCompanies} />}
                        />
                        <Route path="custom-sharing-lists" element={<CustomLists listType={CustomListType.Sharing} />} />
                        <Route path="custom-list/export" element={<Export url={"/CustomListExport"} />} />

                        <Route path="policies" element={<Policies />} />
                        <Route path="policy/:policyId" element={<PolicyOverview />} />
                        <Route path="policy/:policyId/compare/:fromVersionId/:toVersionId" element={<PolicyCompareVersions />} />

                        <Route path="internal-assessments" element={<InternalAssessments />} />

                        <Route path="audit-groups" element={<LegalEntityAuditGroups />} />

                        <Route path="audit-group/:id" element={<LegalEntityAuditGroupOverview />} />
                        <Route path="audit-group/:id/draft" element={<LegalEntityAuditGroupDraftOverview />} />

                        <Route path="assessment-group/:id" element={<InternalAssessmentGroupOverview />} />
                        <Route path="assessment-group/:id/draft" element={<InternalAssessmentGroupDraftOverview />} />

                        <Route path="processing-activity">
                            <Route path=":id">
                                <Route
                                    path="registration-of-research-report-pdf"
                                    element={
                                        <RegistrationOfResearchProjectReport
                                            isPDF
                                            onValidChange={() => {}}
                                            showError={false}
                                            processingActivityStatus={ProcessingActivityStatus.Draft}
                                        />
                                    }
                                />
                                <Route path="internal-acquisition-of-signature-report-pdf" element={<InternalAcquisitionOfSignatureReport isPDF />} />
                                <Route path="research-ethics-approval-report-pdf" element={<ResearchEthicsApprovalReport isPDF />} />
                                <Route
                                    path="approval-of-quality-project-report-pdf"
                                    element={<ApprovalOfQualityProjectReport isPDF processingActivityStatus={ProcessingActivityStatus.Draft} />}
                                />
                            </Route>
                        </Route>

                        <Route path="processing-activity-statistics">
                            <Route path="" element={<ProcessingActivityStatistics />} />
                            <Route path="pdf" element={<ProcessingActivityStatisticsPdf />} />
                        </Route>

                        <Route path="risk-assessment">
                            <Route path=":id">
                                <Route path=":riskAssessmentAreaString">
                                    <Route path=":riskComplianceAreaString">
                                        <Route path=":riskAssessmentVersionId">
                                            <Route path="questions" element={<ProcessingActivityRiskQuestionDialog />} />
                                            <Route path="pdf" element={<ProcessingActivityRiskPDF />} />
                                        </Route>
                                    </Route>
                                </Route>
                            </Route>
                        </Route>

                        <Route path="risk-assessments">
                            <Route path="template" element={<RiskAssessmentTemplate />} />
                            <Route path=":processingActivityId/:riskAssessmentVersionId/scenarios" element={<ProcessingActivityRiskScenariosDialog />} />
                        </Route>

                        <Route path="incident-log">
                            {/* SIMPLIFY when FeatureFlag.IncidentLogTemplates is removed - keep IncidentLogs and delete the legacy IncidentLog page */}
                            <Route path="" element={hasIncidentLogTemplates ? <IncidentLogs /> : <IncidentLog />} />
                            <Route path=":id/overview" element={<IncidentLogOverview />} />
                            {hasIncidentLogTemplates && <Route path=":id/edit" element={<EditIncidentLog />} />}
                        </Route>

                        <Route path="contracts">
                            <Route path="" element={<Contracts />} />
                            <Route path=":id">
                                <Route path="overview" element={<ContractOverview />} />
                                <Route path="edit" element={<Contract />} />
                            </Route>
                        </Route>

                        <Route path="frameworks">
                            <Route path="" element={<Frameworks />} />
                            <Route path=":id">
                                <Route path="overview" element={<FrameworkOverview />} />
                                <Route path="categories" element={<FrameworkCategories />} />

                                <Route path="template">
                                    <Route path="" element={<FrameworkTemplateOverview />} />
                                </Route>
                            </Route>
                            <Route path="templates" element={<Templates />} />
                        </Route>

                        <Route path={"framework-controls"}>
                            <Route path=":id/overview" element={<FrameworkControlOverview />} />
                        </Route>

                        <Route path="advanced-document-search" element={<AdvancedDocumentSearch />} />
                        <Route path="advanced-document-search/export" element={<Export url={"/documents/search/export"} />} />
                    </Route>

                    <Route path="/" element={getPermissionDefaultPage()} />
                    <Route path="*" element={<ErrorPage errorMessage={translateString("pageNotFound")} image="NotFound" />} />
                </Routes>
            </Suspense>
        </AddTaskMenuItemProvider>
    );
}

export default RouterSwitch;
